import type { BadgeTone } from '../components/Badge';
import type {
  ActivityStatus,
  AppStatus,
  BuildStatus,
  DeployPhaseMark,
  DraftReview,
  KnowledgeItemStatus,
  MemoryKind,
  Problem,
  ProposalStatus,
  Publication,
  ReviewSeverity,
  ReviewStage,
  ReviewStatus,
  RevisionAuthor,
  Run,
  RunStatus,
  StudioTrial,
  TaskStatus,
  TrialStatus,
  TurnStatus,
  UploadEngineStatus,
  UploadSchemaStatus,
  UploadStatus,
} from '../api/types';

/**
 * 状態の見せ方を1か所に集める。
 *
 * 特に区別が要るもの:
 * - degraded は「旧状態へ戻った」ではなく「一部だけ変わった」。復旧手段は修正ZIPの再uploadだけ。
 * - outcome_unknown は失敗ではなく成否不明。UIから再実行できない。
 * - reconciling は成功でも失敗でもなく照合中。
 * - run.completed でも endEventId によって業務上の意味が違う。
 */
export interface StatusView {
  tone: BadgeTone;
  label: string;
  /** 状態の意味と、利用者が取れる行動。誤解を招く言い換えをしない。 */
  hint: string | null;
}

export function appStatusView(status: AppStatus): StatusView {
  switch (status) {
    case 'provisioning':
      return { tone: 'active', label: '準備中', hint: '登録処理が進行中です。' };
    case 'ready':
      return { tone: 'completed', label: '利用できます', hint: null };
    case 'updating':
      return {
        tone: 'active',
        label: '配備中',
        hint: '新しい一式へ切り替えています。受付を一時的に止めることがあります。',
      };
    case 'degraded':
      return {
        tone: 'unknown',
        label: 'degraded',
        hint: '一部だけ新しい状態へ変わっています。旧状態へは戻っていません。修正したZIPを再登録してください。',
      };
    case 'draft':
      return { tone: 'neutral', label: '下書き', hint: 'Draft だけの microapp です。Studio で作っています。' };
  }
}

export function runStatusView(status: RunStatus): StatusView {
  switch (status) {
    case 'queued':
      return { tone: 'neutral', label: '受付済み', hint: null };
    case 'running':
      return { tone: 'active', label: '実行中', hint: null };
    case 'completed':
      return { tone: 'completed', label: '終了', hint: null };
    case 'failed':
      return { tone: 'failed', label: '失敗', hint: '技術的な失敗です。業務上の否認とは別です。' };
    case 'cancelled':
      return { tone: 'cancelled', label: '取消', hint: null };
    case 'reconciling':
      return {
        tone: 'unknown',
        label: '照合中',
        hint: '二重に開始しないため、開始したかどうかを照合しています。',
      };
  }
}

/**
 * 終了したrunの業務上の意味。endEventIdでしか区別できないので、
 * status=completed を「成功」と書かない。
 */
export function runOutcomeLabel(run: Run): string {
  if (run.status !== 'completed') return runStatusView(run.status).label;
  return run.endEventId ? `終了イベント ${run.endEventId}` : '終了（終了イベント不明）';
}

export function taskStatusView(status: TaskStatus): StatusView {
  switch (status) {
    case 'queued':
      return { tone: 'neutral', label: '受付済み', hint: null };
    case 'running':
      return { tone: 'active', label: '実行中', hint: null };
    case 'preparing':
      return { tone: 'active', label: '依頼を準備中', hint: null };
    case 'awaiting_answer':
      return { tone: 'awaiting', label: '回答待ち', hint: null };
    case 'processing_answer':
      return { tone: 'active', label: '回答を処理中', hint: null };
    case 'completed':
      return { tone: 'completed', label: '完了', hint: null };
    case 'failed':
      return { tone: 'failed', label: '失敗', hint: '技術的な失敗です。' };
    case 'outcome_unknown':
      return {
        tone: 'unknown',
        label: '成否不明',
        hint: '副作用が確定したか分かりません。自動で再実行しません。業務データを確認してください。',
      };
    case 'cancelled':
      return { tone: 'cancelled', label: '取消', hint: null };
  }
}

export function activityStatusView(status: ActivityStatus): StatusView {
  if (status === 'active') return { tone: 'active', label: '実行中', hint: null };
  return taskStatusView(status);
}

export function uploadStatusView(status: UploadStatus): StatusView {
  switch (status) {
    case 'ready':
      return { tone: 'completed', label: '登録完了', hint: null };
    case 'failed':
      return { tone: 'failed', label: '登録失敗', hint: null };
    case 'queued':
      return { tone: 'neutral', label: '受付済み', hint: null };
    default:
      return { tone: 'active', label: UPLOAD_PHASE_LABELS[status], hint: null };
  }
}

const UPLOAD_PHASE_LABELS: Record<UploadStatus, string> = {
  queued: '受付済み',
  validating: '検証中',
  building: 'ビルド中',
  migrating: 'DDL適用中',
  deploying: '配備中',
  activating: '切替中',
  ready: '登録完了',
  failed: '登録失敗',
};

/** DDLとengineの結果は別々の軸として出す。1本の進捗へ畳むと原因が消える。 */
export function schemaStatusView(status: UploadSchemaStatus): StatusView {
  switch (status) {
    case 'pending':
      return { tone: 'neutral', label: '未実施', hint: null };
    case 'unchanged':
      return { tone: 'completed', label: '変更なし', hint: null };
    case 'applied':
      return { tone: 'completed', label: '適用済み', hint: null };
    case 'partial':
      return { tone: 'unknown', label: '一部適用', hint: '適用できた範囲と残りを確認してください。' };
    case 'unknown':
      return { tone: 'unknown', label: '成否不明', hint: '適用されたか判断できません。' };
    case 'failed':
      return { tone: 'failed', label: '失敗', hint: null };
  }
}

export function engineStatusView(status: UploadEngineStatus): StatusView {
  switch (status) {
    case 'pending':
      return { tone: 'neutral', label: '未実施', hint: null };
    case 'unchanged':
      // 配備する定義が無い app（engine に何も置かないのが正しい結果。BR-1）。
      return { tone: 'completed', label: '配備なし', hint: '配備する定義がありません。' };
    case 'applied':
      return { tone: 'completed', label: '配備済み', hint: null };
    case 'partial':
      return { tone: 'unknown', label: '一部配備', hint: '旧状態へは戻っていません。' };
    case 'unknown':
      return { tone: 'unknown', label: '成否不明', hint: '配備されたか判断できません。' };
    case 'failed':
      return { tone: 'failed', label: '失敗', hint: null };
  }
}

export function knowledgeItemStatusView(status: KnowledgeItemStatus): StatusView {
  switch (status) {
    case 'uploading':
      return { tone: 'active', label: '受付中', hint: 'ファイルの送信と確定を待っています。' };
    case 'ready':
      return { tone: 'completed', label: '利用できます', hint: null };
    case 'upload_failed':
      return { tone: 'failed', label: '失敗', hint: '同じ名前でもう一度追加してください。' };
  }
}

/** Draft の版の作者。 */
export function revisionAuthorView(author: RevisionAuthor): StatusView {
  switch (author) {
    case 'import':
      return { tone: 'neutral', label: '取り込み', hint: '現行のパッケージから取り込んだ版です。' };
    case 'user':
      return { tone: 'active', label: '利用者', hint: 'コードタブで直接編集した版です。' };
    case 'restore':
      return { tone: 'unknown', label: '復元', hint: '過去の版のファイル表に戻した版です。' };
    case 'agent':
      return { tone: 'awaiting', label: 'AI', hint: 'Studio の AI が作った版です。' };
  }
}

/** 版の差分の変更の種類（ファイル・要素は added / removed / modified、出典は gained / lost）。 */
export function diffChangeView(change: string): StatusView {
  switch (change) {
    case 'added':
    case 'gained':
      return { tone: 'completed', label: '追加', hint: null };
    case 'removed':
    case 'lost':
      return { tone: 'failed', label: '削除', hint: null };
    case 'modified':
      return { tone: 'unknown', label: '変更', hint: null };
    default:
      return { tone: 'neutral', label: change, hint: null };
  }
}

/** Studio のターン。作業中・回答待ち・中断を「失敗」と混ぜない。 */
export function turnStatusView(status: TurnStatus): StatusView {
  switch (status) {
    case 'queued':
      return { tone: 'neutral', label: '順番待ち', hint: 'ほかのターンが終わると始まります。' };
    case 'running':
      return { tone: 'active', label: '作業中', hint: 'AI が作業しています。中断できます。' };
    case 'waiting_answer':
      return { tone: 'awaiting', label: '回答待ち', hint: '質問に答えるか、別の指示を書いてください。' };
    case 'done':
      return { tone: 'completed', label: '完了', hint: null };
    case 'failed':
      return { tone: 'failed', label: '失敗', hint: '途中の変更は変更案として残っています。続きから再開できます。' };
    case 'cancelled':
      return { tone: 'cancelled', label: '中断', hint: '途中の変更は変更案として残っています。' };
  }
}

/** 変更案。反映するまで下書きは変わらない。 */
export function proposalStatusView(status: ProposalStatus): StatusView {
  switch (status) {
    case 'pending':
      return { tone: 'unknown', label: '未反映', hint: '反映するまで下書きは変わりません。' };
    case 'interrupted':
      return { tone: 'unknown', label: '中断中', hint: '中断したターンの途中の変更です。' };
    case 'accepted':
      return { tone: 'completed', label: '反映済み', hint: null };
    case 'discarded':
      return { tone: 'cancelled', label: '見送り', hint: null };
  }
}

const MEMORY_KIND_LABELS: Record<MemoryKind, string> = {
  requirement: '要求',
  decision: '決定',
  constraint: '制約',
  preference: '好み',
};

/** 記憶の種類。API の KIND_LABELS（studio/domain/memory.py）と同じ言葉。 */
export function memoryKindView(kind: MemoryKind): string {
  return MEMORY_KIND_LABELS[kind];
}

/** 検証の指摘の重大度。エラーは「試す」を止め、警告は止めない（要件 F-32）。 */
export function findingSeverityView(severity: ReviewSeverity): StatusView {
  switch (severity) {
    case 'error':
      return { tone: 'failed', label: 'エラー', hint: null };
    case 'warning':
      return { tone: 'unknown', label: '警告', hint: null };
  }
}

/** head のレビュー。未検証を「エラーなし」と混ぜない。 */
export function reviewStatusView(status: ReviewStatus): StatusView {
  switch (status) {
    case 'unchecked':
      return { tone: 'neutral', label: '未検証', hint: '検証するとエラーと警告が出ます。' };
    case 'errors':
      return { tone: 'failed', label: 'エラーあり', hint: 'エラーを解消すると試せます。' };
    case 'passed':
      return { tone: 'completed', label: 'エラーなし', hint: null };
  }
}

export function buildStatusView(status: BuildStatus): StatusView {
  switch (status) {
    case 'unchecked':
      return { tone: 'neutral', label: '未 build', hint: null };
    case 'failed':
      return { tone: 'failed', label: 'build 失敗', hint: null };
    case 'passed':
      return { tone: 'completed', label: 'build 済み', hint: null };
  }
}

const REVIEW_STAGE_LABELS: Record<ReviewStage, string> = {
  package: 'パッケージ',
  config: '設定',
  ddl: 'DDL',
  build: 'build',
  declare: '宣言',
  quality: '品質',
};

/** 指摘がどの検査で出たか。 */
export function reviewStageLabel(stage: ReviewStage): string {
  return REVIEW_STAGE_LABELS[stage];
}

/**
 * 「試す」を止めている理由の文。tryBlocked の先頭（基盤が優先順に並べる: エラー → 未検証 → 配備中）で決める。
 * レビューが無ければ null（まだ読んでいない）。
 */
export function tryBlockedText(review: DraftReview | null): string | null {
  if (review === null) return null;
  switch (review.tryBlocked[0]) {
    case 'review_errors':
      return `エラー ${review.report?.errorCount ?? 0} 件を解消すると試せます`;
    case 'review_unchecked':
      return '検証すると試せます';
    case 'deploy_in_progress':
      return '配備の途中です。終わるまで待ってください';
    default:
      return null;
  }
}

/** 段階の移動（PATCH A/draft）の失敗のうち、画面で言い換えるもの。ほかは detail をそのまま出す。 */
export function stageMoveProblemText(problem: Problem): string | null {
  switch (problem.code) {
    case 'stage_incomplete':
      return '次の段階へ進む条件がそろっていません。条件を満たしてからもう一度進めてください。';
    case 'revision_conflict':
      return 'ほかの変更で版が進みました。最新を読み込んでからもう一度操作してください。';
    case 'invalid_stage':
      return '段階は 1 つずつ動かし、戻るときは理由を書いてください。';
    default:
      return null;
  }
}

/**
 * 試運転の状態（S5）。配備（upload）と実行（run）を 1 本の進捗に畳まない。
 * failed は run の技術的な失敗で、業務上の差し戻し（終了イベントの違い）とは別。
 */
export function trialStatusView(status: TrialStatus): StatusView {
  switch (status) {
    case 'needs_attention':
      return { tone: 'failed', label: 'エラーあり・対応待ち', hint: 'Taskのエラーを確認して修正してください。' };
    case 'deploying':
      return { tone: 'active', label: '配備中', hint: '検証・build・DDL 適用・配備を順に進めています。' };
    case 'deploy_failed':
      return { tone: 'failed', label: '配備失敗', hint: '配備が途中で止まりました。理由を直してもう一度試します。' };
    case 'ready':
      return { tone: 'completed', label: '配備済み', hint: 'プロセスを選んで試運転を始められます。' };
    case 'running':
      return { tone: 'active', label: '試運転中', hint: null };
    case 'completed':
      return { tone: 'completed', label: '試運転が完了', hint: null };
    case 'failed':
      return { tone: 'failed', label: '試運転が失敗', hint: '技術的な失敗です。業務上の否認とは別です。' };
    case 'cancelled':
      return { tone: 'cancelled', label: '試運転を取消', hint: null };
  }
}

/** 配備の 4 工程の印。記号だけだと色が伝わらないので tone も返す。 */
export function deployPhaseView(mark: DeployPhaseMark): { symbol: string; tone: BadgeTone } {
  switch (mark) {
    case 'ok':
      return { symbol: '✓', tone: 'completed' };
    case 'fail':
      return { symbol: '✕', tone: 'failed' };
    case 'todo':
      return { symbol: '—', tone: 'neutral' };
    case 'running':
      // engine への配備の結果を確認している途中（BR-1）。まだ終わっていないので ✓ にしない。
      return { symbol: '…', tone: 'active' };
  }
}

/** いま公開している版の名前。版の連番は持たないので公開の version だけを出す（G24）。 */
export function publicationLabel(publication: Publication | null): string {
  return publication === null ? '未公開' : `公開中 v${publication.version}`;
}

/**
 * 「公開する」を止めている理由の文。段階と必須設定では止めない（G13・G26）。
 * 配備済み（head の digest に一致する試運転があり、その配備が終わっている）かどうかを見る。
 * run が失敗した試運転は止めない（公開の条件は配備だけ。G26・G32）。
 */
export function publishBlockedText(review: DraftReview | null): string | null {
  if (review === null) return '下書きがありません';
  if (review.headPublished) return 'この版はすでに公開しています';
  const trial = review.trial;
  if (trial === null || trial.filesDigest !== review.filesDigest) {
    return '公開する前に「試す」で配備してください';
  }
  // 配備が終わっていない試運転の upload は app に当たっていないので、api は 409 not_deployed を返す（G32）。
  if (trial.status === 'deploying') return '配備が終わるまで待ってください';
  if (trial.status === 'deploy_failed') return '配備に失敗しています。もう一度「試す」で配備してください';
  return null;
}

/** 試運転が止まった理由。配備の失敗は upload、実行の失敗は run の detail（英語のまま届く）。 */
export function trialProblemText(trial: StudioTrial | null): string | null {
  if (trial === null) return null;
  // engine への配備が失敗したときは upload の error が空で、理由は api が warnings から作って
  // trial.error に載せる（BR-1）。upload 自体の失敗は今までどおり upload.error にも入っている。
  if (trial.status === 'deploy_failed') return trial.error?.detail ?? trial.upload.error?.detail ?? null;
  if (trial.status === 'needs_attention') return trial.error?.detail ?? null;
  if (trial.status === 'failed') return trial.error?.detail ?? trial.run?.error?.detail ?? null;
  return null;
}

/**
 * app の見出しの状態チップ（Claude Design の app header。G24）。
 * 進行中の配備・試運転を先に見せ、落ち着いていれば公開との関係（同じか、下書きが進んでいるか）を出す。
 * 下書きの連番（「下書き v3」）は持たない。
 */
export function appHeadChipView(review: DraftReview | null): StatusView | null {
  if (review === null) return null;
  const trial = review.trial;
  if (trial !== null) {
    if (trial.status === 'deploying') return trialStatusView('deploying');
    if (trial.status === 'deploy_failed') return trialStatusView('deploy_failed');
    if (trial.status === 'needs_attention') return trialStatusView('needs_attention');
    if (trial.status === 'running') return trialStatusView('running');
  }
  const publication = review.publication;
  // 公開と同じ内容なら、試運転の印は足さない（公開が強い）。
  const tried = !review.headPublished && trial !== null && trial.outcome === 'completed';
  const suffix = tried ? ' · 試運転済み' : '';
  if (publication === null) {
    return { tone: 'neutral', label: `下書き${suffix}`, hint: 'まだ公開していません。' };
  }
  if (review.headPublished) {
    return {
      tone: 'completed',
      label: `公開中 v${publication.version}`,
      hint: '下書きと公開中の内容は同じです。',
    };
  }
  return {
    tone: 'unknown',
    label: `公開中 v${publication.version} · 下書きに変更あり${suffix}`,
    hint: '下書きの変更はまだ公開していません。「試す」で配備してから公開します。',
  };
}

/** uploadが進行中かどうか。完了/失敗まで進捗を取り直す。 */
export function isUploadSettled(status: UploadStatus): boolean {
  return status === 'ready' || status === 'failed';
}
